import { watch, type WatchStopHandle } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import type {
  RouteLocationNormalized,
  RouteLocationRaw,
  Router,
} from "vue-router";

import { attributionGuard } from "@/lib/attribution";
import { setApiRefusalHandler } from "@/api/client";
import { paywallRecovery, VERIFY_PENDING_SEAT } from "@/lib/entitlement";
import { createManifestRoutes } from "@/routing/routerRoutes";
import { useAuthStore } from "@/stores/auth";

/**
 * The production route table is generated from the same manifest as the
 * prerender, sitemap and Pages deep-link outputs. Legacy paths remain manifest
 * redirects because they are live in sent emails and browser history.
 */
const routes = createManifestRoutes();

// Design-system harness for the platform-evolution primitives (rl-58f).
// Dev-only: pushed after the array is built and guarded on import.meta.env.DEV
// so the view is tree-shaken out of production builds entirely — there is no
// route to reach it and no chunk to download.
if (import.meta.env.DEV) {
  routes.push({
    path: "/dev/primitives",
    name: "dev-primitives",
    component: () => import("@/views/DevPrimitivesView.vue"),
  });
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Keep the current route aligned with authorization state that changes after
 * navigation.
 *
 * Role claims move on an ID-token refresh, so re-run the same guard when the
 * session changes rather than waiting for another click. Entitlement state is
 * deliberately absent from claims (§6.4); the earliest truthful signal is the
 * next server refusal, which moves a learner off an entitlement-only route to
 * the recovery flow selected by the reason code.
 */
export function installAuthorizationRouting(
  appRouter: Router = router,
): WatchStopHandle {
  const auth = useAuthStore();
  setApiRefusalHandler(async (error) => {
    if (!appRouter.currentRoute.value.meta.requiresEntitlement) return;

    const recovery = paywallRecovery(error);
    if (recovery === null) return;

    await appRouter.replace(
      recovery === "verify"
        ? VERIFY_PENDING_SEAT
        : recovery === "payer"
          ? "/account"
          : { path: "/", query: { add: "1" } },
    );
  });

  const stopWatchingSession = watch(
    () => [auth.token, auth.roles] as const,
    async () => {
      const current = appRouter.currentRoute.value;
      const decision = await authGuard(current);
      // A newer navigation wins over this asynchronous re-evaluation.
      if (
        decision !== true &&
        current.fullPath === appRouter.currentRoute.value.fullPath
      ) {
        await appRouter.replace(decision);
      }
    },
  );

  return () => {
    stopWatchingSession();
    setApiRefusalHandler(() => {});
  };
}

/**
 * The single navigation guard, exported so it can be exercised directly:
 * assert a redirect.
 */
export async function authGuard(
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | true> {
  const auth = useAuthStore();
  // Wait for the auth client to restore session state from IndexedDB
  // (Firebase) or resolve immediately (mock). Without this, the first
  // navigation on a hard reload sees isAuthenticated=false and
  // redirects to /login before the persisted session is restored.
  await auth.ready;
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: "login", query: { next: to.fullPath } };
  }
  // Not while a sign-up is still creating the account: bouncing off
  // /register then rendered Today against an API that refuses the
  // half-made account, flashing its error before /verify.
  if (to.meta.guestOnly && auth.isAuthenticated && !auth.accountPending) {
    return { name: "root" };
  }
  const requiredPermission = to.meta.requiresPermission;
  if (
    requiredPermission !== undefined &&
    (typeof requiredPermission !== "string" ||
      !auth.hasPermission(requiredPermission))
  ) {
    // Derived from the ID token's `roles` claim through §6.3's client-side
    // presentation matrix. A token refresh therefore ejects a demoted user
    // immediately when installAuthorizationRouting re-evaluates this route.
    //
    // NOT an authorization boundary. Claims are client-side, editable by
    // anyone with devtools, and up to an hour stale. This only decides
    // which routes are worth offering; every endpoint behind the route
    // resolves permissions from Postgres and 403s regardless. Never add an
    // "authorized because the guard passed" assumption downstream of here.
    return { name: "root" };
  }
  return true;
}

// A12 — registered FIRST so the capture happens before any redirect this
// chain performs: a `guestOnly` bounce off `/register` would otherwise
// discard the campaign the visitor arrived on.
router.beforeEach(attributionGuard);
router.beforeEach(authGuard);

export default router;
